/**
 * LeadStatusManagerModal — clinic-wide lead status list (Leads v2 §5).
 * Add / rename / recolor / reorder / delete custom statuses. System statuses
 * (new / converted / lost) are locked. First open while the clinic has no
 * list yet → prefilled from the resolver (imports the user's legacy statuses).
 */
import { useEffect, useMemo, useState } from "react";
import { Tag, Plus, ArrowUp, ArrowDown, Trash, Lock, Info } from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import LoaderLoop from "../shared/loader/LoaderLoop";
import { LEAD_SYSTEM_STATUSES } from "../../utils/leadContract";

const PALETTE = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#6b7280", "#14b8a6", "#f97316"];

function LeadStatusManagerModal({
  isOpen,
  statuses, // resolved list (clinic or merged fallback)
  isImport = false, // clinic has no list yet
  usageCounts = {}, // value → open leads using it
  isSaving = false,
  onClose,
  onSave, // (list) => Promise
}) {
  const [list, setList] = useState([]);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftColor, setDraftColor] = useState(PALETTE[0]);

  useEffect(() => {
    if (!isOpen) return;
    setList((statuses || []).map((s, i) => ({ ...s, order: i })));
    setDraftLabel("");
    setDraftColor(PALETTE[0]);
  }, [isOpen, statuses]);

  const canSave = useMemo(() => list.every((s) => String(s.label || "").trim().length > 0) && !isSaving, [list, isSaving]);

  if (!isOpen) return null;

  const isSystem = (v) => LEAD_SYSTEM_STATUSES.includes(v);
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    [next[idx], next[to]] = [next[to], next[idx]];
    setList(next.map((s, i) => ({ ...s, order: i })));
  };
  const update = (idx, patch) => setList((l) => l.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const remove = (idx) => setList((l) => l.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  const add = () => {
    const label = draftLabel.trim();
    if (!label) return;
    setList((l) => [...l, { value: `custom_${Date.now()}`, label: label.slice(0, 40), color: draftColor, order: l.length }]);
    setDraftLabel("");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ld-modal ld-status-manager" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="tb-modal-title">
            <span className="tb-modal-icon">
              <Tag size={18} weight="fill" />
            </span>
            <div className="ld-modal-heading">
              <span className="ld-eyebrow">מתעניינים</span>
              <h3>ניהול סטטוסים</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="modal-body">
          {isImport && (
            <div className="ld-notice">
              <Info size={16} weight="fill" />
              <span>אלה הסטטוסים הקיימים שלך. שמירה תהפוך אותם למשותפים לכל הקליניקה.</span>
            </div>
          )}

          <div className="ld-status-list">
            {list.map((s, idx) => {
              const locked = isSystem(s.value);
              const used = usageCounts[s.value] || 0;
              return (
                <div key={s.value} className={`ld-status-row${locked ? " is-locked" : ""}`}>
                  <div className="ld-status-reorder">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0 || isSaving} title="הזז למעלה" aria-label="הזז למעלה">
                      <ArrowUp size={13} weight="bold" />
                    </button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === list.length - 1 || isSaving} title="הזז למטה" aria-label="הזז למטה">
                      <ArrowDown size={13} weight="bold" />
                    </button>
                  </div>
                  <label className="ld-color-swatch" title="צבע" style={{ background: s.color }}>
                    <input
                      type="color"
                      value={s.color || "#6b7280"}
                      onChange={(e) => update(idx, { color: e.target.value })}
                      disabled={isSaving}
                      aria-label="צבע הסטטוס"
                    />
                  </label>
                  <input
                    type="text"
                    className="ld-status-label"
                    value={s.label}
                    maxLength={40}
                    disabled={isSaving}
                    onChange={(e) => update(idx, { label: e.target.value })}
                    aria-label="שם הסטטוס"
                  />
                  {used > 0 && <span className="ld-chip ld-chip-neutral" title="מתעניינים פתוחים בסטטוס זה">{used}</span>}
                  {locked ? (
                    <span className="ld-chip ld-chip-neutral ld-locked" title="סטטוס מערכת">
                      <Lock size={11} weight="bold" />
                      נעול
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="ld-icon-btn ld-icon-danger"
                      onClick={() => remove(idx)}
                      disabled={isSaving || used > 0}
                      title={used > 0 ? `לא ניתן למחוק — ${used} מתעניינים פתוחים בסטטוס זה` : "מחק סטטוס"}
                      aria-label="מחק סטטוס"
                    >
                      <Trash size={15} weight="regular" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="ld-status-add">
            <div className="ld-palette">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`ld-palette-dot${draftColor === c ? " is-selected" : ""}`}
                  style={{ background: c }}
                  onClick={() => setDraftColor(c)}
                  aria-label={`צבע ${c}`}
                />
              ))}
            </div>
            <div className="ld-status-add-row">
              <input
                type="text"
                value={draftLabel}
                maxLength={40}
                placeholder="שם סטטוס חדש"
                onChange={(e) => setDraftLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    add();
                  }
                }}
                disabled={isSaving}
              />
              <button type="button" className="btn-secondary" onClick={add} disabled={!draftLabel.trim() || isSaving}>
                <Plus size={15} weight="bold" />
                <span>הוסף</span>
              </button>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isSaving}>
            ביטול
          </button>
          <button className="btn-primary" onClick={() => onSave(list)} disabled={!canSave}>
            {isSaving && <LoaderLoop size="xs" />}
            <span>{isImport ? "שמור כסטטוסי הקליניקה" : "שמור"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default LeadStatusManagerModal;
